import { beautifyWebsite } from "@/features/shops/lib";
import type { Notification } from "@/shared/types/notification";
import type { Shop } from "@/shared/types/shop";

export interface NotificationCodeMeta {
  title: string;
  message: string;
  status: "danger" | "info";
  redirect?: string;
  payload: string[];
}

export const NOTIFICATION_PAGE_SIZE = 20;

/** Utils.NOTIFICATIONS_CODES, copiato a lettera (il redirect "billing-info" non esiste tra le rotte: come oggi). */
export const NOTIFICATION_CODES: Record<string, NotificationCodeMeta> = {
  "generic-error": {
    title: "genericErrorTitle",
    message: "genericErrorMessage",
    status: "danger",
    payload: [],
  },
  "gs-missing-feed": {
    message: "missingGSFeedMessage",
    title: "missingFeed",
    redirect: "setup",
    status: "danger",
    payload: ["shopId", "feedUrl"],
  },
  "missing-feed": {
    message: "missingFeedMessage",
    title: "missingFeed",
    redirect: "setup",
    status: "danger",
    payload: ["shopId", "feedUrl"],
  },
  "gs-bad-feed": {
    title: "badFeed",
    message: "badFeedMessage",
    redirect: "setup",
    status: "danger",
    payload: ["shopId", "feedUrl"],
  },
  "synchronization-failed": {
    title: "errorSync",
    message: "errorSyncMessage",
    redirect: "setup",
    status: "danger",
    payload: ["shopId"],
  },
  "synchronization-complete": {
    title: "completedSync",
    status: "info",
    redirect: "",
    message: "completedSyncMessage",
    payload: ["shopId"],
  },
  "payment-success": {
    title: "paymentSuccess",
    status: "info",
    redirect: "",
    message: "paymentSuccessMessage",
    payload: ["paymentId"],
  },
  "payment-failure": {
    title: "paymentFail",
    status: "danger",
    redirect: "billing-information",
    message: "paymentFailMessage",
    payload: ["paymentId"],
  },
  "plan-switch-success": {
    title: "planSwitchSuccessTitle",
    status: "info",
    redirect: "",
    message: "planSwitchSuccessTitleMessage",
    payload: [],
  },
  "plan-switch-failure": {
    title: "planSwitchFailTitle",
    status: "danger",
    redirect: "billing-info",
    message: "planSwitchFailTitleMessage",
    payload: [],
  },
  "vm-product-fail": {
    title: "vmProductFailTitle",
    status: "danger",
    redirect: "visualMachine",
    message: "vmProductFailMessage",
    payload: [],
  },
};

export function notificationMeta(code: string): NotificationCodeMeta {
  return NOTIFICATION_CODES[code] ?? NOTIFICATION_CODES["generic-error"]!;
}

/** getPayload del legacy: `shopId` diventa `shopName`, ogni altro campo finisce (sovrascrivendosi) in `key`. */
export function notificationPayload(notification: Notification, shops: Shop[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  notificationMeta(notification.code).payload.forEach((key) => {
    if (key === "shopId") {
      const shop = shops.find((item) => String(item.id) === String(notification.payload.shopId));
      result.shopName = beautifyWebsite(shop ? shop.url : "");
    } else result.key = notification.payload[key];
  });
  return result;
}

type Translate = (key: string, values?: Record<string, unknown>) => string;

/** timeDifference del legacy. */
export function timeAgo(elapsed: number, t: Translate): string {
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;
  if (elapsed < msPerMinute) return t("notificationsModal.sago", { var1: Math.round(elapsed / 1000) });
  if (elapsed < msPerHour) return t("notificationsModal.mago", { var1: Math.round(elapsed / msPerMinute) });
  if (elapsed < msPerDay) return t("notificationsModal.hago", { var1: Math.round(elapsed / msPerHour) });
  if (elapsed < msPerMonth) return t("notificationsModal.dago", { var1: Math.round(elapsed / msPerDay) });
  if (elapsed < msPerYear) return t("notificationsModal.moago", { var1: Math.round(elapsed / msPerMonth) });
  return t("notificationsModal.yago", { var1: Math.round(elapsed / msPerYear) });
}
